import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/auth';
import { DIVISION_FILTER_PARAM } from '@features/account-view/scope';
import OrdersService from '@utility/services/order';
import { orderKeys } from '../lib/query-keys';
import type { OrderListParams } from '../lib/list-params';
import type { StatusCountRow } from '../lib/status-tabs';

/*
 * EN-4117: what the page's own numbers (title, tab capsules) are told on V2.
 * `rows` is undefined before the first successful count. A background refresh
 * (including a failed one) keeps the cached numbers; the sidebar is never read.
 */
export type V2StatusCounts = {
  rows: StatusCountRow[] | undefined;
  loading: boolean;
};

/*
 * What the page's numbers show before `/live` says which source serves it:
 * nothing yet, so a V2 account never flashes the sidebar's numbers.
 */
export const COUNTS_NOT_KNOWN: V2StatusCounts = {
  rows: undefined,
  loading: true,
};

/*
 * The counts a page shows: none until the source is known, the V2 counts on
 * V2, and null (the sidebar) on a legacy account.
 */
export const pageCounts = (
  known: boolean,
  isV2: boolean,
  v2Counts: V2StatusCounts,
): V2StatusCounts | null =>
  !known ? COUNTS_NOT_KNOWN : isV2 ? v2Counts : null;

/* Paging and ordering do not narrow a count, so they do not restart it. */
const NOT_A_COUNT_FILTER = [
  'status',
  'page',
  'limit',
  'sort',
  'sort_by',
  'sort_order',
  /* Legacy fulfilment sub-tabs do not narrow V2 counts. */
  'tab',
  /* The service sends the active division scope, also held in the cache key. */
  DIVISION_FILTER_PARAM,
];

function toCountFilters(params: OrderListParams): OrderListParams {
  const filters = { ...params };
  for (const key of NOT_A_COUNT_FILTER) {
    delete filters[key];
  }
  /* URL defaults, cleared controls and absent values mean the same thing.
   * In particular the list spells an unset oos as null, the URL as false. */
  for (const [key, value] of Object.entries(filters)) {
    if (value == null || value === '' || (key === 'oos' && value === false)) {
      delete filters[key];
    }
  }

  return filters;
}

/*
 * The V2 list's own filters, minus the status, against `GET /status/count`.
 * Keyed under `orderKeys.all`, so the invalidation `useOrdersLive` runs when
 * the version moves refetches it too.
 */
export function useV2StatusCounts(
  listParams: OrderListParams | undefined,
  isV2: boolean,
  /* Read the URL just as the page does, before its list initializer runs.
   * This selects the exact cached filter set even during a route remount. */
  initialFilters: OrderListParams = {},
): V2StatusCounts {
  const { currentOrg } = useAuth();
  const filters = toCountFilters(listParams ?? initialFilters);
  const query = useQuery({
    queryKey: [...orderKeys.statusCounts(filters), currentOrg],
    queryFn: () => OrdersService.statusCount(filters),
    enabled: isV2 && !!currentOrg && !!listParams,
    retry: false,
    // /live records a changed version even when this refresh fails. Retry at
    // its 10s cadence until counts recover, without polling healthy counts.
    refetchInterval: q => (q.state.status === 'error' ? 10_000 : false),
  });

  return {
    rows: query.data,
    loading: !query.data && !query.isError,
  };
}

/* Null on a legacy account: the sidebar stays the source there. */
const V2StatusCountsContext = createContext<V2StatusCounts | null>(null);

export const V2StatusCountsProvider = V2StatusCountsContext.Provider;

export const useV2StatusCountsState = () => useContext(V2StatusCountsContext);
